import { CloudflareKVCache } from './cloudflareKVCache';
import { createLogger } from './logger';

const logger = createLogger({ module: 'HybridCache', level: 'warn' });

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  userId: string;
  ttl: number;
  accessCount: number; // Track access frequency for intelligent promotion
  lastAccessed: number; // Track last access time
}

interface CacheOptions {
  ttl?: number;
  dependencies?: string[];
  version?: string;
}

interface CacheResult<T> {
  data: T | null;
  status: 'valid' | 'expired' | 'not_found' | 'stale_offline' | 'stale_fallback' | 'fresh';
  source: 'l1_cache' | 'l2_cache' | 'miss' | 'l1_miss' | 'l1_expired' | 'offline_cache' | 'fallback_cache' | 'network';
  age?: number;
  promoted?: boolean; // Indicates if data was promoted from L2 to L1
}

interface CacheMetrics {
  l1HitRate: number;
  l2HitRate: number;
  promotionRate: number;
  evictionRate: number;
  averageResponseTime: number;
  totalOperations: number;
}

export class HybridCache {
  private l1Cache = new Map<string, CacheEntry<any>>();
  private l2Cache = new CloudflareKVCache();
  private readonly L1_TTL = 60000; // 1 minute
  private readonly L2_TTL = 300000; // 5 minutes
  private readonly MAX_L1_SIZE = 100; // Maximum number of entries in L1 cache
  private readonly PROMOTION_THRESHOLD = 2; // Promote to L1 after 2 L2 hits
  
  // Performance tracking
  private metrics = {
    l1Hits: 0,
    l2Hits: 0,
    misses: 0,
    promotions: 0,
    evictions: 0,
    totalResponseTime: 0,
    operationCount: 0
  };
  
  constructor() {
    logger.info('Enhanced Hybrid cache initialized with intelligent promotion and parallel operations');
    
    // Set up periodic L1 cache cleanup and metrics reporting
    this.startL1Cleanup();
    this.startMetricsReporting();
  }

  async get<T>(key: string, userId: string): Promise<CacheResult<T>> {
    const startTime = performance.now();
    
    // L1: Check memory cache first (fastest)
    const l1Result = this.getFromL1<T>(key, userId);
    if (l1Result.status === 'valid') {
      this.recordMetric('l1_hit', performance.now() - startTime);
      logger.debug('L1 cache hit', { key, userId: userId.substring(0, 8) });
      return l1Result;
    }
    
    // L2: Check Cloudflare KV cache with intelligent promotion
    const l2Result = await this.l2Cache.get<T>(key, userId);
    if (l2Result.status === 'valid' && l2Result.data) {
      // Intelligent promotion: promote frequently accessed data to L1
      const shouldPromote = await this.shouldPromoteToL1(key, userId);
      
      if (shouldPromote) {
        this.setInL1(key, l2Result.data, userId, true); // Mark as promoted
        this.metrics.promotions++;
        logger.debug('L2 cache hit with promotion to L1', { key, userId: userId.substring(0, 8) });
      } else {
        logger.debug('L2 cache hit without promotion', { key, userId: userId.substring(0, 8) });
      }
      
      this.recordMetric('l2_hit', performance.now() - startTime);
      return {
        data: l2Result.data,
        status: 'valid',
        source: 'l2_cache',
        age: l2Result.age,
        promoted: shouldPromote
      };
    }
    
    this.recordMetric('miss', performance.now() - startTime);
    logger.debug('Cache miss', { key, userId: userId.substring(0, 8) });
    return {
      data: null,
      status: 'not_found',
      source: 'miss'
    };
  }
  
  async set<T>(
    key: string, 
    data: T, 
    userId: string,
    options: CacheOptions = {}
  ): Promise<void> {
    const {
      ttl = this.L2_TTL,
      dependencies = [],
      version = '1.0'
    } = options;
    
    // Parallel cache operations for better performance
    const [l1Result, l2Result] = await Promise.allSettled([
      this.setInL1Async(key, data, userId),
      this.l2Cache.set(key, data, userId, { ttl, dependencies, version })
    ]);
    
    // Log any failures but don't throw
    if (l1Result.status === 'rejected') {
      logger.warn('L1 cache set failed:', { key, error: l1Result.reason });
    }
    if (l2Result.status === 'rejected') {
      logger.warn('L2 cache set failed:', { key, error: l2Result.reason });
    }
    
    logger.debug('Data cached in parallel (L1 + L2)', { 
      key, 
      userId: userId.substring(0, 8),
      ttl,
      l1Success: l1Result.status === 'fulfilled',
      l2Success: l2Result.status === 'fulfilled'
    });
  }
  
  async invalidate(pattern: string, userId: string): Promise<void> {
    // Parallel invalidation for better performance
    const [l1Result, l2Result] = await Promise.allSettled([
      Promise.resolve(this.invalidateL1(pattern, userId)),
      this.l2Cache.invalidate(pattern, userId)
    ]);
    
    // Log any failures
    if (l1Result.status === 'rejected') {
      logger.warn('L1 cache invalidation failed:', { pattern, error: l1Result.reason });
    }
    if (l2Result.status === 'rejected') {
      logger.warn('L2 cache invalidation failed:', { pattern, error: l2Result.reason });
    }
    
    logger.debug('Cache invalidated in parallel (L1 + L2)', { 
      pattern, 
      userId: userId.substring(0, 8),
      l1Success: l1Result.status === 'fulfilled',
      l2Success: l2Result.status === 'fulfilled'
    });
  }
  
  async clear(userId: string): Promise<void> {
    // Clear both L1 and L2 caches
    this.clearL1(userId);
    
    try {
      await this.l2Cache.clear(userId);
      logger.debug('Cleared both L1 and L2 caches', { userId: userId.substring(0, 8) });
    } catch (error) {
      logger.error('Failed to clear L2 cache:', error);
      // L1 is still cleared
    }
  }
  
  async getStaleData<T>(key: string, userId: string): Promise<T | null> {
    // Try both caches in parallel for stale data
    const [l1Promise, l2Promise] = await Promise.allSettled([
      Promise.resolve(this.getStaleFromL1<T>(key, userId)),
      this.l2Cache.getStaleData<T>(key, userId)
    ]);
    
    // Prefer L1 stale data if available
    if (l1Promise.status === 'fulfilled' && l1Promise.value) {
      logger.debug('Stale L1 data retrieved', { key, userId: userId.substring(0, 8) });
      return l1Promise.value;
    }
    
    // Fall back to L2 stale data
    if (l2Promise.status === 'fulfilled' && l2Promise.value) {
      // Populate L1 with stale data for faster access
      this.setInL1(key, l2Promise.value, userId);
      logger.debug('Stale L2 data retrieved and promoted to L1', { key, userId: userId.substring(0, 8) });
      return l2Promise.value;
    }
    
    return null;
  }
  
  // Enhanced cache statistics with performance metrics
  async getCacheStats(userId: string): Promise<{
    l1Stats: {
      totalKeys: number;
      totalSize: number;
      hitRate: number;
      averageAccessCount: number;
    };
    l2Stats: {
      totalKeys: number;
      totalSize: number;
      oldestEntry: number | null;
      newestEntry: number | null;
      hitRate: number;
    };
    performance: CacheMetrics;
    combined: {
      totalKeys: number;
      estimatedTotalSize: number;
      overallHitRate: number;
    };
  }> {
    // L1 stats with enhanced metrics
    const l1Keys = Array.from(this.l1Cache.keys())
      .filter(key => key.startsWith(`${userId}:`));
    
    let l1Size = 0;
    let totalAccessCount = 0;
    l1Keys.forEach(key => {
      const entry = this.l1Cache.get(key);
      if (entry) {
        l1Size += JSON.stringify(entry).length;
        totalAccessCount += entry.accessCount;
      }
    });
    
    // L2 stats
    const l2Stats = await this.l2Cache.getCacheStats(userId);
    
    // Performance metrics
    const totalHits = this.metrics.l1Hits + this.metrics.l2Hits;
    const totalOperations = totalHits + this.metrics.misses;
    
    const performance: CacheMetrics = {
      l1HitRate: totalOperations > 0 ? (this.metrics.l1Hits / totalOperations) * 100 : 0,
      l2HitRate: totalOperations > 0 ? (this.metrics.l2Hits / totalOperations) * 100 : 0,
      promotionRate: this.metrics.l2Hits > 0 ? (this.metrics.promotions / this.metrics.l2Hits) * 100 : 0,
      evictionRate: this.metrics.operationCount > 0 ? (this.metrics.evictions / this.metrics.operationCount) * 100 : 0,
      averageResponseTime: this.metrics.operationCount > 0 ? this.metrics.totalResponseTime / this.metrics.operationCount : 0,
      totalOperations
    };
    
    return {
      l1Stats: {
        totalKeys: l1Keys.length,
        totalSize: l1Size,
        hitRate: performance.l1HitRate,
        averageAccessCount: l1Keys.length > 0 ? totalAccessCount / l1Keys.length : 0
      },
      l2Stats: {
        ...l2Stats,
        hitRate: performance.l2HitRate
      },
      performance,
      combined: {
        totalKeys: l1Keys.length + l2Stats.totalKeys,
        estimatedTotalSize: l1Size + l2Stats.totalSize,
        overallHitRate: totalOperations > 0 ? (totalHits / totalOperations) * 100 : 0
      }
    };
  }
  
  async healthCheck(): Promise<{
    l1Available: boolean;
    l2Available: boolean;
    l2Latency?: number;
    l2Error?: string;
    overall: boolean;
    performance: {
      averageResponseTime: number;
      operationsPerSecond: number;
    };
  }> {
    // L1 is always available (in-memory)
    const l1Available = true;
    
    // Check L2 (Cloudflare KV) with performance timing
    const l2Health = await this.l2Cache.healthCheck();
    
    // Calculate operations per second (last minute)
    const opsPerSecond = this.metrics.operationCount / 60; // Rough estimate
    
    return {
      l1Available,
      l2Available: l2Health.available,
      l2Latency: l2Health.latency,
      l2Error: l2Health.error,
      overall: l1Available && l2Health.available,
      performance: {
        averageResponseTime: this.metrics.operationCount > 0 ? this.metrics.totalResponseTime / this.metrics.operationCount : 0,
        operationsPerSecond: opsPerSecond
      }
    };
  }
  
  // Intelligent cache promotion logic
  private async shouldPromoteToL1(key: string, userId: string): Promise<boolean> {
    // Check if we have room in L1
    if (this.l1Cache.size >= this.MAX_L1_SIZE) {
      // Only promote if this item is more valuable than the least valuable L1 item
      const leastValuable = this.findLeastValuableL1Entry(userId);
      if (!leastValuable) return false;
      
      // Simple heuristic: promote if this would be accessed more frequently
      // In a real implementation, you might track access patterns in L2
      return true; // For now, always promote if we can make room
    }
    
    return true; // Promote if we have room
  }
  
  private findLeastValuableL1Entry(userId: string): string | null {
    let leastValuableKey: string | null = null;
    let lowestScore = Infinity;
    
    for (const [key, entry] of this.l1Cache.entries()) {
      if (!key.startsWith(`${userId}:`)) continue;
      
      // Score based on access frequency and recency
      const timeSinceAccess = Date.now() - entry.lastAccessed;
      const score = entry.accessCount / (timeSinceAccess / 1000); // Access per second
      
      if (score < lowestScore) {
        lowestScore = score;
        leastValuableKey = key;
      }
    }
    
    return leastValuableKey;
  }
  
  private getFromL1<T>(key: string, userId: string): CacheResult<T> {
    const cacheKey = `${userId}:${key}`;
    const entry = this.l1Cache.get(cacheKey);
    
    if (!entry || entry.userId !== userId) {
      return { data: null, status: 'not_found', source: 'l1_miss' };
    }
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    if (age > this.L1_TTL) {
      this.l1Cache.delete(cacheKey);
      return { 
        data: null, 
        status: 'expired', 
        source: 'l1_expired',
        age 
      };
    }
    
    // Update access tracking
    entry.accessCount++;
    entry.lastAccessed = now;
    
    return {
      data: entry.data,
      status: 'valid',
      source: 'l1_cache',
      age
    };
  }
  
  private getStaleFromL1<T>(key: string, userId: string): T | null {
    const cacheKey = `${userId}:${key}`;
    const entry = this.l1Cache.get(cacheKey);
    
    if (entry && entry.userId === userId) {
      // Update access tracking even for stale data
      entry.accessCount++;
      entry.lastAccessed = Date.now();
      return entry.data;
    }
    
    return null;
  }
  
  private setInL1<T>(key: string, data: T, userId: string, isPromotion = false): void {
    const cacheKey = `${userId}:${key}`;
    
    // Check if we need to evict entries to stay under size limit
    if (this.l1Cache.size >= this.MAX_L1_SIZE) {
      const evictedKey = this.evictLeastValuableL1Entry(userId);
      if (evictedKey) {
        this.metrics.evictions++;
      }
    }
    
    const now = Date.now();
    this.l1Cache.set(cacheKey, {
      data,
      timestamp: now,
      userId,
      ttl: this.L1_TTL,
      accessCount: isPromotion ? this.PROMOTION_THRESHOLD : 1,
      lastAccessed: now
    });
  }
  
  private async setInL1Async<T>(key: string, data: T, userId: string): Promise<void> {
    return Promise.resolve(this.setInL1(key, data, userId));
  }
  
  private evictLeastValuableL1Entry(userId: string): string | null {
    const leastValuableKey = this.findLeastValuableL1Entry(userId);
    
    if (leastValuableKey) {
      this.l1Cache.delete(leastValuableKey);
      logger.debug('Evicted least valuable L1 cache entry', { key: leastValuableKey });
      return leastValuableKey;
    }
    
    return null;
  }
  
  private recordMetric(type: 'l1_hit' | 'l2_hit' | 'miss', responseTime: number): void {
    this.metrics.operationCount++;
    this.metrics.totalResponseTime += responseTime;
    
    switch (type) {
      case 'l1_hit':
        this.metrics.l1Hits++;
        break;
      case 'l2_hit':
        this.metrics.l2Hits++;
        break;
      case 'miss':
        this.metrics.misses++;
        break;
    }
  }
  
  private startMetricsReporting(): void {
    // Report metrics every 5 minutes
    setInterval(() => {
      if (this.metrics.operationCount > 0) {
        const totalHits = this.metrics.l1Hits + this.metrics.l2Hits;
        const hitRate = (totalHits / (totalHits + this.metrics.misses)) * 100;
        
        logger.info('Cache performance metrics', {
          hitRate: hitRate.toFixed(2) + '%',
          l1HitRate: ((this.metrics.l1Hits / this.metrics.operationCount) * 100).toFixed(2) + '%',
          l2HitRate: ((this.metrics.l2Hits / this.metrics.operationCount) * 100).toFixed(2) + '%',
          promotions: this.metrics.promotions,
          evictions: this.metrics.evictions,
          averageResponseTime: (this.metrics.totalResponseTime / this.metrics.operationCount).toFixed(2) + 'ms',
          totalOperations: this.metrics.operationCount
        });
        
        // Reset metrics for next period
        this.metrics = {
          l1Hits: 0,
          l2Hits: 0,
          misses: 0,
          promotions: 0,
          evictions: 0,
          totalResponseTime: 0,
          operationCount: 0
        };
      }
    }, 300000); // 5 minutes
  }
  
  private startL1Cleanup(): void {
    // Clean up expired L1 entries every minute
    setInterval(() => {
      const now = Date.now();
      const keysToDelete: string[] = [];
      
      for (const [key, entry] of this.l1Cache.entries()) {
        if (now - entry.timestamp > this.L1_TTL) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => this.l1Cache.delete(key));
      
      if (keysToDelete.length > 0) {
        logger.debug('L1 cache cleanup completed', { 
          expiredEntries: keysToDelete.length,
          remainingEntries: this.l1Cache.size 
        });
      }
    }, 60000); // Every minute
  }
  
  private invalidateL1(pattern: string, userId: string): void {
    const keysToDelete = Array.from(this.l1Cache.keys())
      .filter(key => key.startsWith(`${userId}:`) && key.includes(pattern));
    
    keysToDelete.forEach(key => this.l1Cache.delete(key));
    
    logger.debug('L1 cache invalidated', { 
      pattern, 
      userId: userId.substring(0, 8),
      keysDeleted: keysToDelete.length 
    });
  }
  
  private clearL1(userId: string): void {
    const keysToDelete = Array.from(this.l1Cache.keys())
      .filter(key => key.startsWith(`${userId}:`));
    
    keysToDelete.forEach(key => this.l1Cache.delete(key));
    
    logger.debug('L1 cache cleared for user', { 
      userId: userId.substring(0, 8),
      keysDeleted: keysToDelete.length 
    });
  }

  /**
   * Preload data into cache using a fetch function
   */
  async preload<T>(
    key: string, 
    userId: string, 
    fetchFn: () => Promise<T>, 
    options: CacheOptions = {}
  ): Promise<T> {
    const startTime = performance.now();
    
    try {
      // Check if data already exists in cache
      const existing = await this.get<T>(key, userId);
      if (existing.status === 'valid') {
        logger.debug('Data already cached, skipping preload', { key, source: existing.source });
        return existing.data!;
      }
      
      // Fetch fresh data
      const data = await fetchFn();
      
      // Store in cache
      await this.set(key, data, userId, options);
      
      const duration = performance.now() - startTime;
      this.recordMetric('miss', duration);
      
      logger.debug('Data preloaded successfully', { 
        key, 
        duration: Math.round(duration),
        userId: userId.substring(0, 8)
      });
      
      return data;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordMetric('miss', duration);
      
      logger.error('Failed to preload data:', { key, error, duration: Math.round(duration) });
      throw error;
    }
  }
}

// Export singleton instance
export const hybridCache = new HybridCache(); 